<template>
  <div
    id="echarts-box"
    ref="echartsBoxRef"
    :style="{
      width: boxW,
      height: boxH
    }"
  />
</template>

<script setup lang="ts">
import echarts, { type ECOption } from './setupEcharts';
import { type EChartsType } from 'echarts/core';
import { useElementSize } from '@vueuse/core';
import { onBeforeUnmount, onMounted, PropType, Ref, ref, shallowRef, watch, watchEffect } from 'vue';

defineOptions({ name: 'CustomEchartsBox' });

const props = defineProps({
  // 图表配置
  option: {
    type: Object as PropType<ECOption>,
    default: () => ({} as ECOption),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  // 盒子宽度
  boxW: {
    type: String,
    default: '100%',
  },
  // 盒子高度
  boxH: {
    type: String,
    default: '400px',
  },
});

// echarts图表的容器
const echartsBoxRef = ref<Ref<HTMLDivElement> | any>();
// 监听盒子容器宽高变化
const { width, height } = useElementSize(echartsBoxRef);
// 当前echarts图表实例
const echartsInstance = shallowRef<EChartsType>();

/**
 * @function 初始化图表
 */
const init = () => {
  if (!echartsBoxRef.value) return;

  if (!echartsInstance.value) {
    echartsInstance.value = echarts.init(echartsBoxRef.value, null, { devicePixelRatio: 2 });
    handleLoading(props.loading);
    draw();
  }
};

/**
 * @function 绘制图表
 */
const draw = () => {
  echartsInstance.value && echartsInstance.value.setOption(props.option);
};
/**
 * @function 绘制图表
 */
const handleLoading = (loading: boolean) => {
  if (typeof loading === 'boolean') {
    let loadingOpt = {
      text: '',
      color: '#4679fa',
      textColor: '#4679fa',
      fontSize: 14,
      spinnerRadius: 10, //
      lineWidth: 2,
    };

    loading ? echartsInstance.value?.showLoading(loadingOpt) : echartsInstance.value?.hideLoading();
  }
};

onMounted(() => {
  init();
});

onBeforeUnmount(() => {
  // 容器被销毁之后，销毁实例，避免内存泄漏
  echartsInstance.value?.dispose();
});

watchEffect(() => {
  if (width.value) {
    echartsInstance.value?.resize();
  }
  
  if (height.value) {
    echartsInstance.value?.resize();
  }
});

// 配置发生改变，重新绘制
watch(
  () => props.option,
  (n) => {
    draw();
  },
  { deep: true },
);
// 图表loading
watch(
  () => props.loading,
  (loading) => {
    handleLoading(loading);
  },
);

/**
 * @function 获取echarts实例
 */
const getEcInstance = () => echartsInstance.value ?? null;

defineExpose({ getEcInstance });
</script>